import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { AirtableAPI, TABLE_IDS, CURRENT_USER } from '../globals.js'
import { F, computeEnrichment } from '../utils.jsx'
import { filterLoadedData, TEAM_TABLES } from '../commercialScope.js'
import { APP_TABLES, settleTables } from '../dataLoad.js'
import { withoutTestRecords } from '../testRecords.js'

const INITIAL_DATA = {
  accounts: [], stakeholders: [], opportunities: [], actionPlan: [],
  outreach: [], solutions: [], events: [], clientPartners: [], sources: [],
  icp: [], proposals: [], campaigns: [], contentLab: [], landings: [],
  loadErrors: {},
}

// Same rule as the server (src/commercialScope.js), which already filtered what it sent
function applyRoleFilter(results) {
  return filterLoadedData(results, CURRENT_USER)
}

export function useAppData() {
  const [data, setData] = useState(INITIAL_DATA)
  // The last data shown (also after local edits): a background refresh that fails keeps it
  const dataRef = useRef(INITIAL_DATA)
  useEffect(() => { dataRef.current = data }, [data])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [api, setApi] = useState(null)

  const loadData = useCallback(async (apiInstance, silent = false) => {
    if (!silent) setLoading(true)
    if (silent) setRefreshing(true)
    try {
      // The team of the central base reads only its tables (the server refuses the rest): the others stay empty
      // on purpose, not as a failed load
      const tables = CURRENT_USER?.teamMode ? APP_TABLES.filter(t => TEAM_TABLES.includes(t.key)) : APP_TABLES
      const keys = tables.map(t => t.key)
      // Each table on its own: one that fails is reported (loadErrors), never shown as empty
      const settled = await Promise.allSettled(tables.map(t => apiInstance.fetchTable(TABLE_IDS[t.table])))
      const { results, errors, missing } = settleTables(keys, settled, dataRef.current)
      for (const t of APP_TABLES) if (!Array.isArray(results[t.key])) results[t.key] = []

      results.opportunities = (results.opportunities || []).filter(o => {
        const name = F(o, 'Name') || F(o, 'Opportunity') || ''
        return name.toLowerCase() !== 'none'
      })

      applyRoleFilter(results)
      setData({ ...computeEnrichment(results), loadErrors: errors, missingTables: missing, loadedAt: new Date().toISOString() })
    } catch (e) {
      console.error('Load failed:', e)
      // Nothing could be processed: keep what was shown and say so
      setData(prev => ({ ...prev, loadErrors: Object.fromEntries(APP_TABLES.map(t => [t.key, 'No se pudo procesar'])) }))
    }
    if (!silent) setLoading(false)
    if (silent) setRefreshing(false)
  }, [])

  // The team of the central base: what is shown follows the current assignment. The server already refuses any read
  // or write of an account that was reassigned; this also takes it off the screen (on return to the tab and every
  // 2 minutes), so an old list does not stay open as if it were still theirs.
  useEffect(() => {
    if (!CURRENT_USER?.teamMode || !api) return
    const refresh = () => { if (document.visibilityState === 'visible') loadData(api, true) }
    const timer = setInterval(refresh, 120000)
    document.addEventListener('visibilitychange', refresh)
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', refresh) }
  }, [api, loadData])

  const initApi = useCallback(() => {
    const a = new AirtableAPI()
    setApi(a)
    return a
  }, [])

  const addToData = useCallback((tableKey, fields, realId = null) => {
    const recordId = realId || ('tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7))
    setData(prev => ({
      ...prev,
      [tableKey]: [...(prev[tableKey] || []), { id: recordId, fields }],
    }))
  }, [])

  const updateInData = useCallback((tableKey, recordId, updatedFields) => {
    setData(prev => ({
      ...prev,
      [tableKey]: (prev[tableKey] || []).map(r =>
        r.id === recordId ? { ...r, fields: { ...r.fields, ...updatedFields } } : r
      ),
    }))
  }, [])

  const removeFromData = useCallback((tableKey, recordId) => {
    setData(prev => ({
      ...prev,
      [tableKey]: (prev[tableKey] || []).filter(r => r.id !== recordId),
    }))
  }, [])

  // Without the records of the acceptance checks ("PRUEBA — …", src/testRecords.js): for every metric,
  // report, follow-up list, campaign and message; `data` keeps them so they can be found and worked on
  const reportingData = useMemo(() => withoutTestRecords(data), [data])

  return {
    data, reportingData, loading, refreshing, api,
    loadData, initApi,
    addToData, updateInData, removeFromData,
  }
}
